import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2 } from "lucide-react";

interface Business {
  id: string;
  name: string;
  slug: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  openingHours?: {
    monday?: string;
    tuesday?: string;
    wednesday?: string;
    thursday?: string;
    friday?: string;
    saturday?: string;
    sunday?: string;
  };
}

const businessFormSchema = z.object({
  name: z.string().min(2, {
    message: "Business name must be at least 2 characters.",
  }),
  slug: z.string().min(2, {
    message: "Slug must be at least 2 characters.",
  }),
  description: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  website: z.string().url().optional().or(z.literal("")),
  openingHours: z
    .object({
      monday: z.string().optional(),
      tuesday: z.string().optional(),
      wednesday: z.string().optional(),
      thursday: z.string().optional(),
      friday: z.string().optional(),
      saturday: z.string().optional(),
      sunday: z.string().optional(),
    })
    .optional(),
});

export default function Settings() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { user, isSignedIn, isLoaded } = useUser();
  const { session } = useClerk();
  const [activeTab, setActiveTab] = useState("general");

  // Function to get auth headers
  const getAuthHeaders = async () => {
    if (!session) {
      console.error("No active session");
      return {};
    }

    const token = await session.getToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const { data: business, isLoading: isLoadingBusiness } = useQuery({
    queryKey: ["business"],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await axios.get("/api/businesses/my", { headers });
      return response.data as Business;
    },
    enabled: !!isSignedIn && !!session,
  });

  const form = useForm<z.infer<typeof businessFormSchema>>({
    resolver: zodResolver(businessFormSchema),
    defaultValues: {
      name: "",
      slug: "",
      description: "",
      address: "",
      phone: "",
      email: "",
      website: "",
      openingHours: {
        monday: "",
        tuesday: "",
        wednesday: "",
        thursday: "",
        friday: "",
        saturday: "",
        sunday: "",
      },
    },
  });

  // Update form with business data when it loads
  React.useEffect(() => {
    if (business) {
      form.reset({
        name: business.name,
        slug: business.slug,
        description: business.description || "",
        address: business.address || "",
        phone: business.phone || "",
        email: business.email || "",
        website: business.website || "",
        openingHours: business.openingHours || {
          monday: "",
          tuesday: "",
          wednesday: "",
          thursday: "",
          friday: "",
          saturday: "",
          sunday: "",
        },
      });
    }
  }, [business, form]);

  const updateBusinessMutation = useMutation({
    mutationFn: async (values: z.infer<typeof businessFormSchema>) => {
      const headers = await getAuthHeaders();
      return axios.patch(`/api/businesses/${business?.id}`, values, {
        headers,
      });
    },
    onSuccess: () => {
      toast.success(t("settings.businessUpdated"));
    },
    onError: (error) => {
      console.error("Error updating business:", error);
      toast.error(t("settings.updateError"));
    },
  });

  const onSubmit = (values: z.infer<typeof businessFormSchema>) => {
    updateBusinessMutation.mutate(values);
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>{t("common.loading")}</p>
      </div>
    );
  }

  if (!isSignedIn) {
    navigate("/sign-in");
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">{t("settings.title")}</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="general">{t("settings.general")}</TabsTrigger>
          <TabsTrigger value="business">{t("settings.business")}</TabsTrigger>
          <TabsTrigger value="reservations">
            {t("settings.reservations")}
          </TabsTrigger>
          <TabsTrigger value="notifications">
            {t("settings.notifications")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.account")}</CardTitle>
              <CardDescription>
                {t("settings.accountDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-16 h-16 rounded-full bg-gray-200 mr-4 overflow-hidden">
                    {user?.imageUrl && (
                      <img
                        src={user.imageUrl}
                        alt={user.fullName || ""}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium">{user?.fullName}</h3>
                    <p className="text-sm text-muted-foreground">
                      {user?.primaryEmailAddress?.emailAddress}
                    </p>
                  </div>
                </div>

                <div>
                  <Button
                    variant="outline"
                    onClick={() => window.open("/user/account", "_blank")}
                  >
                    {t("settings.manageAccount")}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t("settings.accountManagementDescription")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="business">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.businessInformation")}</CardTitle>
              <CardDescription>
                {t("settings.businessInfoDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingBusiness ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("settings.businessName")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="slug"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("settings.businessSlug")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormDescription>
                              {t("settings.slugDescription")}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("settings.description")}</FormLabel>
                          <FormControl>
                            <Textarea rows={4} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("settings.address")}</FormLabel>
                          <FormControl>
                            <Textarea rows={2} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("settings.phone")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("settings.email")}</FormLabel>
                            <FormControl>
                              <Input type="email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="website"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("settings.website")}</FormLabel>
                            <FormControl>
                              <Input type="url" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={updateBusinessMutation.isPending}
                      className="flex items-center gap-2"
                    >
                      {updateBusinessMutation.isPending && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {t("settings.saveChanges")}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reservations">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.reservationSettings")}</CardTitle>
              <CardDescription>
                {t("settings.reservationSettingsDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">
                      {t("settings.enableReservations")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.enableReservationsDescription")}
                    </p>
                  </div>
                  <Switch />
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">
                      {t("settings.reservationTimeSlots")}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="slot-duration">
                          {t("settings.slotDuration")}
                        </Label>
                        <Input
                          id="slot-duration"
                          type="number"
                          defaultValue={60}
                          min={15}
                          step={15}
                        />
                      </div>
                      <div>
                        <Label htmlFor="advance-booking">
                          {t("settings.advanceBooking")}
                        </Label>
                        <Input
                          id="advance-booking"
                          type="number"
                          defaultValue={30}
                          min={1}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">
                      {t("settings.businessHours")}
                    </h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t("settings.businessHoursDescription")}
                    </p>

                    <div className="space-y-2">
                      {[
                        "monday",
                        "tuesday",
                        "wednesday",
                        "thursday",
                        "friday",
                        "saturday",
                        "sunday",
                      ].map((day) => (
                        <div key={day} className="flex items-center space-x-2">
                          <div className="w-24">
                            <Label>{t(`settings.days.${day}`)}</Label>
                          </div>
                          <div>
                            <Input
                              placeholder="e.g. 9:00-17:00 or Closed"
                              className="w-64"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <Button>{t("settings.saveChanges")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.notificationSettings")}</CardTitle>
              <CardDescription>
                {t("settings.notificationSettingsDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">
                      {t("settings.emailNotifications")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.emailNotificationsDescription")}
                    </p>
                  </div>
                  <Switch defaultChecked />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">
                      {t("settings.smsNotifications")}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {t("settings.smsNotificationsDescription")}
                    </p>
                  </div>
                  <Switch />
                </div>

                <div className="pt-4">
                  <Button>{t("settings.saveChanges")}</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
